/**
 * The search panel's own behaviour, on top of what `<details>` and Shopify's
 * `<predictive-search>` already do between them.
 *
 * Four jobs, and the first two are the ones that matter — both are the same
 * idea, that the two most valuable things a customer can type here are the two
 * this catalogue's own search answers worst:
 *
 * - **A registration reaches the fitment engine.** Nothing in the catalogue can
 *   match a reg — no product title contains one — so without this the single
 *   most valuable thing a UK driver could type is the one query guaranteed to
 *   return nothing. Pressing Enter on a reg goes straight to the finder; a reg
 *   recognised mid-typing offers the hand-off as a row ABOVE the results rather
 *   than instead of them, so a false positive costs nothing.
 * - **A CAR NAME offers the car.** Worse than matching nothing: "kuga" matches
 *   plenty, across three generations, and hands the customer a page of 30-word
 *   fulfilment strings to work out which is theirs — the exact guess the
 *   fitment layer exists to remove, on the one screen that was still making
 *   them do it. `fitment-index.json` already knows every car we cover, so a
 *   car-shaped query offers those cars, and a click lands on `/pages/my-car`
 *   with `?car=<handle>` — resolved, named, priced.
 * - The field takes focus when the panel opens, or the panel opened for nothing.
 * - The scrim closes it, which is what everyone tries first.
 *
 * 🔴 The whole file is an IIFE, and it has to be: the SEARCH RESULTS page loads
 * this script twice — once from `sections/airflux-search.liquid` and once from
 * the header panel in the layout above it — so every top-level `const` here is
 * declared twice in the same global scope. The `customElements.get()` guard at
 * the bottom used to be enough because there was nothing else at the top level;
 * the moment the car matcher added `fold` and friends, the second execution
 * died on `Identifier 'fold' has already been declared` before reaching it.
 */
(() => {
  'use strict';

/**
 * Diacritics off, punctuation to spaces, lower case.
 *
 * 🔴 The `normalize('NFD')` is load-bearing, not tidiness. Two of the makes we
 * cover are Citroën and Škoda, and nobody types either of those characters into
 * a search box — without this, "citroen berlingo" and "skoda octavia" match
 * nothing at all while their products sit in the results underneath.
 */
const fold = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * How many rows are still worth offering. Above this the query named a MAKE,
 * not a car — "ford" is 43 rows and "audi" 50 — and a list of five arbitrary
 * Fords is worse than no list, because the products underneath are the better
 * answer to that question. Six leaves room for the widest real car query:
 * measured, the deepest model in the catalogue is four rows (BMW X5, Lexus RX,
 * VW Golf).
 */
const CAR_MATCH_LIMIT = 6;
const CAR_SHOW = 5;

/**
 * Which cars a query names.
 *
 * A year is pulled out first and used as a FILTER rather than as a word, so
 * "kuga 2020" narrows three Kuga rows to one instead of matching a row that
 * happens to have 2020 in its text. It only narrows when something survives —
 * a year nobody covers falls back to the model's rows rather than to silence.
 *
 * Every remaining token must match some word of the row, and the rule differs
 * by length for one measured reason:
 *
 *   1 character  EXACT word. "mercedes c class" was returning 32 rows because
 *                "c" prefix-matched the word "class" in GLC-Class, EQB-Class
 *                and every other one. As an exact word it returns 2.
 *   2+           PREFIX, so "berling" finds the Berlingo and "transp" the
 *                Transporter. Anything stricter fails half of real typing.
 *
 * Newest first: a search for a model name is far more often about the car on
 * the drive than the one sold in 2004.
 */
const CAR_ALIAS = { vw: 'volkswagen', merc: 'mercedes', landrover: 'land rover', chevy: 'chevrolet' };

/**
 * The same arrow the plate offer above these rows carries, and it is here for
 * the same reason: this row does not filter the page, it LEAVES it, and it
 * changes the car the whole site is about on the way. Built as a node rather
 * than as markup because everything else in this list is (the labels come out
 * of `sessionStorage`), and `{% render 'airflux-icon' %}` cannot reach a row
 * that JavaScript creates.
 */
function arrowSvg() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'af-i af-search__car-go');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M3 8h10M9 4l4 4-4 4');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  return svg;
}

function matchCars(rows, query) {
  let year = null;
  const text = fold(query)
    .replace(/\b(19|20)\d{2}\b/g, (m) => {
      year = Number(m);
      return ' ';
    })
    .replace(/\s+/g, ' ')
    .trim();

  const tokens = text
    .split(' ')
    .filter(Boolean)
    .flatMap((t) => (CAR_ALIAS[t] || t).split(' '));
  if (!tokens.length) return [];

  let hits = rows.filter((r) =>
    tokens.every((t) => r.words.some((w) => (t.length === 1 ? w === t : w.startsWith(t))))
  );
  if (year) {
    const inYear = hits.filter((r) => year >= r.v.f && year <= r.v.t);
    if (inYear.length) hits = inYear;
  }
  if (!hits.length || hits.length > CAR_MATCH_LIMIT) return [];
  return [...hits].sort((a, b) => b.v.t - a.v.t).slice(0, CAR_SHOW);
}

if (!customElements.get('airflux-search')) {
  customElements.define(
    'airflux-search',
    class AirfluxSearch extends HTMLElement {
      connectedCallback() {
        // `<details>` only in the header panel. On the results page the field is
        // just on the page, and everything below works the same without it.
        this.details = this.querySelector('details');
        this.input = this.querySelector('input[type="search"]');
        this.form = this.querySelector('form');
        this.offer = this.querySelector('[data-af-plate-offer]');
        this.offerLabel = this.querySelector('[data-af-plate-offer-label]');
        this.carOffer = this.querySelector('[data-af-car-offer]');
        this.carList = this.querySelector('[data-af-car-list]');
        if (!this.input) return;

        this.details?.addEventListener('toggle', () => {
          if (this.details.open) this.input.focus();
          else {
            this.showOffer(null);
            this.showCars([]);
          }
        });

        this.querySelector('[data-af-search-scrim]')?.addEventListener('click', () => {
          this.details.open = false;
        });

        // A reg carried here in the URL — someone submitted one before the
        // script had loaded. Offer the hand-off rather than leaving them on a
        // results page that by definition found nothing.
        this.check();

        // `input`, not the debounced `change` predictive search listens for:
        // the offer is a local string test, so it can keep up with typing.
        this.input.addEventListener('input', () => this.check());
        this.form?.addEventListener('submit', (event) => this.onSubmit(event));
        this.offer?.addEventListener('click', () => this.handOff());
      }

      get plate() {
        return window.AirfluxPlate;
      }

      check() {
        const value = this.input.value.trim();
        const isReg = !!this.plate?.looksLikeReg(value);
        this.showOffer(isReg ? value : null);
        // A reg is already being handed off; asking the index about it as well
        // would only ever return nothing.
        if (isReg) this.showCars([]);
        else this.checkCars(value);
      }

      // ── cars ─────────────────────────────────────────────────────────────

      /**
       * The index, fetched at most once and only when something car-shaped has
       * been typed.
       *
       * 🔴 Lazily, and this is the whole reason it is written here rather than
       * imported: the panel is in the header of EVERY page and the index is
       * 173KB. Loading it on page load to serve a feature most visits never use
       * would cost every visit the thing this theme spends its effort saving.
       *
       * The `sessionStorage` key is `airflux-fitment.js`'s, deliberately — on a
       * landing page or the home page the finder has already put the index
       * there, so this reads it for free and the two never fetch twice. The key
       * carries the asset URL because Shopify's `?v=` changes on every sync,
       * and a fixed key served an open tab a stale copy for the whole session.
       */
      loadIndex() {
        if (this._indexPromise) return this._indexPromise;
        const url = this.dataset.indexUrl;
        if (!url) return Promise.resolve(null);
        const cacheKey = `airflux:index:${url}`;

        this._indexPromise = (async () => {
          try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return JSON.parse(cached);
          } catch {
            // A corrupt or unreadable copy is not worth a failed fetch.
          }
          try {
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify(data));
            } catch {
              // Quota. The copy in memory is still good for this page.
            }
            return data;
          } catch {
            // No retry ladder here, unlike the finder's: this is an offer on top
            // of a search that still works. Silence is the right failure.
            return null;
          }
        })();
        return this._indexPromise;
      }

      /**
       * Every car row, flattened once per index, into the two things matching
       * needs: the words a query is tested against, and the label a row shows.
       *
       * The label is the Years dropdown's, verbatim — years · generation · body
       * — because it IS that choice asked earlier, and because two BMW 2 Series
       * rows share a year range and differ only by body. Dropping either would
       * put two identical-looking rows in front of the customer.
       */
      carRows(index) {
        if (this._rows) return this._rows;
        this._rows = index.vehicles.map((v) => {
          const name = `${index.makes[v.m]} ${v.n}`;
          return {
            v,
            name,
            label: [`${v.f}–${v.t}`, v.g, v.b].filter(Boolean).join(' · '),
            words: fold(`${name} ${v.g || ''} ${v.b || ''}`).split(' ').filter(Boolean),
          };
        });
        return this._rows;
      }

      async checkCars(value) {
        // Two characters is the floor: below it every query matches half the
        // catalogue, and the fetch would fire on the first keystroke.
        if (fold(value).length < 2) return this.showCars([]);

        const index = await this.loadIndex();
        // The field has moved on while the index was in flight — never paint an
        // answer to a question that is no longer on screen.
        if (!index || this.input.value.trim() !== value) return;
        this.showCars(matchCars(this.carRows(index), value));
      }

      /**
       * 🔴 Built as nodes, not markup, and the href is checked before it is set.
       *
       * The index is our own asset, but it is fetched JSON that has been through
       * `sessionStorage` — the same rule the rest of the theme follows for
       * anything that has been out of the page and back.
       */
      showCars(rows) {
        if (!this.carOffer || !this.carList) return;
        if (!rows.length) {
          this.carOffer.hidden = true;
          this.carList.replaceChildren();
          return;
        }

        const destination = this.dataset.carUrl || this.dataset.homeUrl || '/';
        this.carList.replaceChildren(
          ...rows.map((r) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.className = 'af-search__car';
            // 🔴 One destination for every car, rather than the two scoped
            // landing pages: a car with both categories — which most search hits
            // have — cannot be answered by choosing one of them, and choosing
            // for the customer is the guess this whole layer exists to remove.
            //
            // That destination used to be the HOME page, because the home hero's
            // finder is the one scoped "all" and `?car=` there resolves into the
            // router. It was the right answer on the wrong page: somebody who
            // typed a car into a search box and pressed a row was returned to
            // the front door, with what they asked for tucked inside a hero
            // built to ask a question they had just answered. Searching should
            // move you forward. `/pages/my-car` is that same answer at page
            // size — see `sections/airflux-car-result.liquid`.
            a.href = `${destination}?car=${encodeURIComponent(r.v.h)}`;

            const name = document.createElement('span');
            name.className = 'af-search__car-name';
            name.textContent = r.name;

            const meta = document.createElement('span');
            meta.className = 'af-search__car-meta';
            meta.textContent = r.label;

            // The two labels share a wrapper so the qualifier can drop to a
            // second line on its own when the row runs out of width, instead of
            // shouldering the arrow off the end of the panel. "Mercedes-Benz
            // C-Class 2014–2021 · W205 · 4-door Saloon" is the row that found
            // this, and it is a real one.
            const text = document.createElement('span');
            text.className = 'af-search__car-text';
            text.append(name, meta);

            a.append(text, arrowSvg());
            li.appendChild(a);
            return li;
          })
        );
        this.carOffer.hidden = false;
      }

      /**
       * The plate itself is `aria-hidden` — it is a picture of a number plate,
       * and read out it would be eight letters spelled at random. The button's
       * name has to carry the registration instead, or a screen reader hears
       * "find the parts that fit it" with no antecedent for "it".
       */
      showOffer(value) {
        if (!this.offer) return;
        this.offer.hidden = !value;
        if (!value) return;

        const reg = this.plate.format(value);
        this.offerLabel.textContent = reg;
        this.offer.setAttribute(
          'aria-label',
          `${reg} — ${this.querySelector('.af-search__plate-text')?.textContent.trim()}`
        );
      }

      /**
       * Enter on a full registration means "find my car", not "search the
       * catalogue for this string" — the second of those cannot succeed.
       */
      onSubmit(event) {
        const value = this.input.value.trim();
        if (!this.plate?.looksLikeReg(value)) return;
        event.preventDefault();
        this.handOff();
      }

      handOff() {
        const value = this.input.value.trim();
        if (this.details) this.details.open = false;
        // The car page over the home page for the same reason the rows above
        // use it, and it carries a finder of its own — so a reg typed here on a
        // page with none lands somewhere that can look it up AND somewhere whose
        // whole subject is the answer.
        this.plate?.handOff(value, { home: this.dataset.carUrl || this.dataset.homeUrl || '/' });
      }
    }
  );
}
})();
